'use client';

/**
 * Full-height stats slider — each slide is a centred headline and
 * description over a row of big numbers ("3.000.000+ Kullanıcı"), on a
 * dark background lit by an optional radial glow. Two or more slides add
 * the prev/next arrows and dots pinned to the bottom centre.
 *
 * <pre>
 *   ┌──────────────────────────────────────────────────────────┐
 *   │              Türkiye’nin *yerli ve milli*                │
 *   │                 çevrim içi platformu!                    │
 *   │                                                          │
 *   │               Two-line description copy                  │
 *   │                                                          │
 *   │   3.000.000+        14+          400+        195.000+    │
 *   │   Kullanıcı         Kategori     Eğitim      Dakika      │
 *   │                     ░░ glow ░░                           │
 *   └──────────────────────────────────────────────────────────┘
 * </pre>
 *
 * <p>Type defaults follow the design (40px title, 18px Regular
 * description, 45px SemiBold values, 25px Regular #BEBEBE labels) and are
 * all authorable: family, size, weight and colour per text role. With no
 * family set the block uses the theme's font. Wrapping part of the title
 * in asterisks ({@code *yerli ve milli*}) sets it in italic.
 *
 * <p>Full height, the carousel behaviour and the pinned controls come
 * from {@link FullHeightSlider}; the glow is its backdrop, so it stays put
 * while slides move over it.
 */
import { useMemo, type CSSProperties } from 'react';
import {
  isVisible,
  useBlockPart,
  useCurrentUser,
  type CurrentUser,
} from '@akademi/site-builder-runtime';
import type { CampaignAudience } from '../_shared/campaignAudience';
import { asList, tx } from '../_shared/coerce';
import {
  FullHeightFrame,
  FullHeightSlider,
  type FullHeightSliderSettings,
} from '../_shared/FullHeightSlider';
import { CountUpText } from './countUp';
import { withEmphasis } from '../_shared/emphasis';

type Localized = string | Record<string, string>;

export interface FullHeightStatsItem {
  /** The big number, e.g. "3.000.000+". Localized so each language can
   *  format its own thousands separator. */
  value?: Localized;
  label?: Localized;
}

export interface FullHeightStatsSlide extends CampaignAudience {
  /** `*asterisks*` around a phrase set it in italic. */
  title?: Localized;
  description?: Localized;
  stats?: FullHeightStatsItem[];
}

export interface FullHeightStatsProps extends FullHeightSliderSettings {
  slides?: FullHeightStatsSlide[];
  /** Heading level of the FIRST slide's title — later slides always `h2`. */
  titleTag?: 'h1' | 'h2';

  backgroundColor?: string;
  showGlow?: boolean;
  glowColor?: string;

  /** CSS font family for every text role. Empty = the theme's font. */
  fontFamily?: string;
  titleFontSize?: number;
  titleFontWeight?: string;
  titleColor?: string;
  descriptionFontSize?: number;
  descriptionFontWeight?: string;
  descriptionColor?: string;
  valueFontSize?: number;
  valueFontWeight?: string;
  valueColor?: string;
  labelFontSize?: number;
  labelFontWeight?: string;
  labelColor?: string;

  /** Count each stat value up from 0 the first time it's on screen. */
  countUp?: boolean;
  /** Length of the count-up in ms. */
  countUpDuration?: number;
}

// Font sizes ride CSS variables so the breakpoints below can scale whatever
// the author picked; margins use vh so a short screen tightens the rhythm
// instead of pushing the stats off the fold (the vh line is the fallback for
// engines without dvh).
const FULL_HEIGHT_STATS_CSS = `
.fhs-title { font-size: var(--fhs-title-size); }
.fhs-desc {
  font-size: var(--fhs-desc-size);
  margin: clamp(20px, 6.85vh, 66px) 0 0;
  margin-top: clamp(20px, 6.85dvh, 66px);
}
.fhs-stats {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 40px 32px;
  width: 100%;
  max-width: 1410px;
  margin: clamp(48px, 17.7vh, 171px) 0 0;
  margin-top: clamp(48px, 17.7dvh, 171px);
  padding: 0;
  list-style: none;
}
.fhs-value { font-size: var(--fhs-value-size); }
.fhs-label { font-size: var(--fhs-label-size); }
@media (max-width: 1023px) {
  .fhs-stats {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 40px 24px;
    max-width: 640px;
  }
  .fhs-title { font-size: calc(var(--fhs-title-size) * 0.8); }
  .fhs-value { font-size: calc(var(--fhs-value-size) * 0.8); }
}
@media (max-width: 767px) {
  .fhs-title { font-size: calc(var(--fhs-title-size) * 0.7); }
  .fhs-desc { font-size: calc(var(--fhs-desc-size) * 0.89); }
  .fhs-value { font-size: calc(var(--fhs-value-size) * 0.6); }
  .fhs-label { font-size: calc(var(--fhs-label-size) * 0.64); }
}
`;

/** Keeps an author-typed family inside its declaration. */
const cleanFamily = (family?: string) => (family ?? '').replace(/[;{}<>]/g, '').trim();

/** Form fields can hand numbers over as strings ("52"). */
const px = (n: unknown, fallback: number) => {
  const v = Number(n);
  return `${Number.isFinite(v) && v > 0 ? v : fallback}px`;
};

/** Radial falloff (strength at distance, as % of the farthest side). */
const GLOW_STOPS: Array<[number, number]> = [
  [100, 0],
  [83, 27],
  [58, 45],
  [35, 63],
  [19, 80],
  [8, 100],
];

const glowGradient = (color: string) =>
  `radial-gradient(circle farthest-side at 50% 79%, ${GLOW_STOPS.map(([strength, at]) =>
    strength === 100
      ? `${color} ${at}%`
      : `color-mix(in srgb, ${color} ${strength}%, transparent) ${at}%`,
  ).join(', ')}, transparent 120%)`;

const statsOf = (s: FullHeightStatsSlide) =>
  asList<FullHeightStatsItem>(s.stats).filter((it) => it && (tx(it.value) || tx(it.label)));

const hasContent = (s: FullHeightStatsSlide) =>
  Boolean(tx(s.title) || tx(s.description) || statsOf(s).length > 0);

export function FullHeightStats({
  slides: rawSlides,
  titleTag = 'h2',
  backgroundColor = '#000000',
  showGlow = true,
  glowColor = '#111a34',
  fontFamily,
  titleFontSize = 40,
  titleFontWeight = '600',
  titleColor = '#ffffff',
  descriptionFontSize = 18,
  descriptionFontWeight = '400',
  descriptionColor = '#ffffff',
  valueFontSize = 45,
  valueFontWeight = '600',
  valueColor = '#ffffff',
  labelFontSize = 25,
  labelFontWeight = '400',
  labelColor = '#BEBEBE',
  countUp = true,
  countUpDuration = 2000,
  ...settings
}: FullHeightStatsProps) {
  const currentUser = useCurrentUser<CurrentUser>();
  const part = useBlockPart();

  const slides = useMemo(
    () =>
      (Array.isArray(rawSlides) ? rawSlides : [])
        .filter((s) => s && hasContent(s))
        .filter((s) => isVisible(s, currentUser)),
    [rawSlides, currentUser],
  );

  const family = cleanFamily(fontFamily);
  const bodyFamily = family
    ? `${family}, var(--font-family-body, sans-serif)`
    : 'var(--font-family-body, inherit)';
  const titleFamily = family
    ? `${family}, var(--font-family-display, sans-serif)`
    : 'var(--font-family-display, inherit)';
  const titleWeight = Number(titleFontWeight) || 600;

  const typeVars = {
    '--fhs-title-size': px(titleFontSize, 40),
    '--fhs-desc-size': px(descriptionFontSize, 18),
    '--fhs-value-size': px(valueFontSize, 45),
    '--fhs-label-size': px(labelFontSize, 25),
  } as CSSProperties;

  // Sampled from the design: a navy light centred low (50% / 79%) that falls
  // off like a soft blur — full strength at the core, a long faint tail.
  // `circle farthest-side` keeps it round at any aspect ratio (half the
  // width on desktop, most of the height on a phone).
  const glow = showGlow ? (
    <div style={{ position: 'absolute', inset: 0, background: glowGradient(glowColor) }} />
  ) : null;

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: FULL_HEIGHT_STATS_CSS }} />
      <FullHeightSlider
        {...settings}
        slides={slides}
        background={backgroundColor}
        backdrop={glow}
        renderSlide={(slide, i, { active, hasControls }) => {
          const TitleTag = i === 0 && titleTag === 'h1' ? 'h1' : 'h2';
          const titleText = tx(slide.title);
          const descriptionText = tx(slide.description);
          const stats = statsOf(slide);
          return (
            <FullHeightFrame
              hasControls={hasControls}
              style={{
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                // The design sits the content 18px above true centre; the
                // extra bottom padding lifts it by half of that.
                paddingBottom: (hasControls ? 80 : 40) + 36,
                ...typeVars,
              }}
            >
              {titleText ? (
                <TitleTag
                  {...part('title', i)}
                  className="fhs-title"
                  style={{
                    margin: 0,
                    maxWidth: 700,
                    fontFamily: titleFamily,
                    fontWeight: titleWeight,
                    lineHeight: 1.24,
                    color: titleColor,
                    whiteSpace: 'pre-line',
                  }}
                >
                  {withEmphasis(titleText, Math.min(900, titleWeight + 100))}
                </TitleTag>
              ) : null}
              {descriptionText ? (
                <p
                  {...part('subtitle', i)}
                  className="fhs-desc"
                  style={{
                    maxWidth: 700,
                    fontFamily: bodyFamily,
                    fontWeight: Number(descriptionFontWeight) || 400,
                    lineHeight: 1.2,
                    color: descriptionColor,
                    whiteSpace: 'pre-line',
                    ...(titleText ? null : { marginTop: 0 }),
                  }}
                >
                  {descriptionText}
                </p>
              ) : null}
              {stats.length > 0 ? (
                <ul
                  {...part('list', i)}
                  className="fhs-stats"
                  style={titleText || descriptionText ? undefined : { marginTop: 0 }}
                >
                  {stats.map((stat, j) => (
                    <li
                      key={j}
                      {...part('item', j)}
                      style={{ minWidth: 0, textAlign: 'left', fontFamily: bodyFamily }}
                    >
                      {tx(stat.value) ? (
                        <div
                          className="fhs-value"
                          style={{
                            fontWeight: Number(valueFontWeight) || 600,
                            lineHeight: 1.21,
                            color: valueColor,
                            overflowWrap: 'anywhere',
                          }}
                        >
                          <CountUpText
                            text={tx(stat.value)}
                            enabled={countUp}
                            active={active}
                            duration={countUpDuration}
                          />
                        </div>
                      ) : null}
                      {tx(stat.label) ? (
                        <div
                          className="fhs-label"
                          style={{
                            marginTop: 1,
                            fontWeight: Number(labelFontWeight) || 400,
                            lineHeight: 1.21,
                            color: labelColor,
                          }}
                        >
                          {tx(stat.label)}
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : null}
            </FullHeightFrame>
          );
        }}
      />
    </>
  );
}
